import React from 'react';
import {
  AccommodationBookingEvent,
  AccommodationBookingEventType,
  BookingFinancialChange,
  BookingFinancialChangeMetadata,
} from '../../../../types/accommodation';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  checked_in: 'Checked in',
  checked_out: 'Checked out',
  cancelled: 'Cancelled',
  no_show: 'No show',
};

const CHANGE_EVENT_TITLES: Record<
  Exclude<AccommodationBookingEventType, 'status_change'>,
  string
> = {
  rate_updated: 'Room rate updated',
  payment_updated: 'Payment updated',
  payment_deleted: 'Payment deleted',
};

const formatChangeValue = (
  change: BookingFinancialChange,
  value: BookingFinancialChange['from'],
  currency: string,
) => {
  if (value === null || value === undefined || value === '') return '—';
  if (change.kind === 'amount') {
    return `${currency} ${Number(value).toLocaleString()}`;
  }
  if (change.kind === 'date') {
    return new Date(String(value)).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }
  return String(value);
};

const getEventTitle = (ev: AccommodationBookingEvent) => {
  if (ev.event_type && ev.event_type !== 'status_change') {
    return CHANGE_EVENT_TITLES[ev.event_type];
  }
  if (!ev.to_status) return 'Updated';
  return ev.from_status
    ? `${STATUS_LABELS[ev.from_status] ?? ev.from_status} → ${STATUS_LABELS[ev.to_status] ?? ev.to_status}`
    : (STATUS_LABELS[ev.to_status] ?? ev.to_status);
};

const FinancialChangeDetails: React.FC<{ ev: AccommodationBookingEvent }> = ({
  ev,
}) => {
  const metadata = (ev.metadata ?? {}) as BookingFinancialChangeMetadata;
  const currency = metadata.currency ?? '';
  const changes = metadata.changes ?? [];
  const isDeletion = ev.event_type === 'payment_deleted';

  return (
    <div className="mt-1.5 space-y-1 rounded-lg border border-gray-100 bg-slate-50/60 px-2.5 py-2 text-xs">
      {changes.map((change) => (
        <p key={change.label} className="text-gray-600">
          <span className="font-medium text-gray-700">{change.label}:</span>{' '}
          <span className="text-gray-400 line-through">
            {formatChangeValue(change, change.from, currency)}
          </span>
          {isDeletion ? null : (
            <>
              {' → '}
              <span className="font-semibold text-pos-primary">
                {formatChangeValue(change, change.to, currency)}
              </span>
            </>
          )}
        </p>
      ))}
      {metadata.total_before !== metadata.total_after ? (
        <p className="text-gray-500">
          Total: {currency} {Number(metadata.total_before).toLocaleString()} →{' '}
          {currency} {Number(metadata.total_after).toLocaleString()}
        </p>
      ) : null}
      {metadata.paid_before !== metadata.paid_after ? (
        <p className="text-gray-500">
          Paid: {currency} {Number(metadata.paid_before).toLocaleString()} →{' '}
          {currency} {Number(metadata.paid_after).toLocaleString()}
        </p>
      ) : null}
    </div>
  );
};

interface Props {
  events: AccommodationBookingEvent[];
}

export const BookingEventTimeline: React.FC<Props> = ({ events }) => {
  if (events.length === 0) {
    return <p className="text-sm text-gray-400">No events recorded yet.</p>;
  }

  return (
    <ol className="relative border-l border-gray-200 pl-5">
      {events.map((ev, idx) => {
        const actor = ev.triggered_by_user
          ? `${ev.triggered_by_user.first_name} ${ev.triggered_by_user.last_name}`
          : 'System';
        const date = new Date(ev.created_at).toLocaleString(undefined, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        const isChange = Boolean(
          ev.event_type && ev.event_type !== 'status_change',
        );
        return (
          <li key={ev.id} className={idx < events.length - 1 ? 'mb-5' : ''}>
            <span
              className={`absolute -left-1.5 mt-1 h-3 w-3 rounded-full border-2 border-white ${
                isChange ? 'bg-amber-500' : 'bg-pos-primary'
              }`}
            />
            <p className="text-xs text-gray-400">{date}</p>
            <p className="mt-0.5 text-sm font-medium text-gray-900">
              {getEventTitle(ev)}
            </p>
            <p className="text-xs text-gray-500">by {actor}</p>
            {isChange ? <FinancialChangeDetails ev={ev} /> : null}
            {ev.notes && (
              <p className="mt-1 text-xs text-gray-400">
                {isChange ? `Reason: ${ev.notes}` : ev.notes}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
};
